import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { registerSW } from 'virtual:pwa-register';
import { ClerkProvider } from '@clerk/clerk-react';
import { trTR } from '@clerk/localizations';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';
import App from './App.tsx';
import { applyAccessibilitySettings, getAccessibilitySettings } from './utils/accessibility';

// Erişilebilirlik ayarlarını uygulama başlangıcında yükle
applyAccessibilitySettings(getAccessibilitySettings());

registerSW({ immediate: false });

// Handle Vite dynamic import chunk loading errors (e.g. after new deployments)
window.addEventListener('vite:preloadError', () => {
  console.warn('[Vite Preload Error]: Dynamic module import failed. Reloading page...');
  window.location.reload();
});

if ('serviceWorker' in navigator) {
  // sessionStorage kullanıyoruz çünkü reload sonrası JS değişkenleri sıfırlanır —
  // autoUpdate modunda yeni SW her aktive olduğunda controllerchange tekrar tetiklenip
  // sonsuz reload döngüsüne girebiliyordu. Bayrak, oturum başına tek reload'a sınırlıyor.
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!sessionStorage.getItem('sw_reload_done')) {
      sessionStorage.setItem('sw_reload_done', 'true');
      window.location.reload();
    }
  });
}

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string;

if (!PUBLISHABLE_KEY) {
  console.warn('[clerk] VITE_CLERK_PUBLISHABLE_KEY eksik.');
}

// Localhost geliştirme ortamı tespiti
const isLocalhost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

// Anahtarın production (pk_live) olup olmadığını kontrol et
const isProductionKey = PUBLISHABLE_KEY?.startsWith('pk_live_');



// EKRAN TASARIMI: UYARI COMPONENT'İ
const WarningScreen = ({ title, message, subMessage }: { title: string, message: string, subMessage?: string }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      fontFamily: 'Inter, system-ui, sans-serif',
      backgroundColor: '#f8fafc',
      color: '#0f172a',
      padding: '20px',
      textAlign: 'center',
    }}
  >
    <div style={{ padding: '2rem', background: '#fff', borderRadius: '1rem', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)' }}>
      <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '16px', color: '#dc2626' }}>
        ⚠️ {title}
      </h2>
      <p style={{ marginBottom: '12px', fontSize: '16px', fontWeight: '500' }}>
        {message}
      </p>
      {subMessage && (
        <p style={{ maxWidth: '600px', lineHeight: '1.6', color: '#475569', fontSize: '14px', marginTop: '16px', padding: '1rem', background: '#f1f5f9', borderRadius: '0.5rem' }}>
          {subMessage}
        </p>
      )}
    </div>
  </div>
);

import { ToastProvider } from './components/Toast';

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <HelmetProvider>
      {!PUBLISHABLE_KEY ? (
        <WarningScreen 
          title="Sistem Yapılandırması Eksik" 
          message="VITE_CLERK_PUBLISHABLE_KEY çevre değişkeni bulunamadı." 
          subMessage="Cloudflare Pages ortamı için Dashboard > Settings > Environment Variables bölümüne anahtarı ekleyip yeniden deploy edin." 
        />
      ) : isLocalhost && isProductionKey ? (
        <WarningScreen 
          title="Geliştirici Uyarısı: Localhost'ta Canlı Anahtar Kullanılamaz!" 
          message="Şu anda Localhost (Geliştirme) ortamındasınız, ancak VITE_CLERK_PUBLISHABLE_KEY olarak bir Canlı (Production) anahtarı ('pk_live_...') kullanıyorsunuz." 
          subMessage="Clerk güvenlik ve CORS politikaları gereği, canlı anahtarlar yalnızca onaylı domainler üzerinden (örn: bitmiyo.com.tr) çalışır. Localhost üzerinden istek attığınızda Clerk kütüphanesi çöker ve Beyaz Ekrana (White Screen of Death) sebep olur. Lütfen .env dosyanızda Geliştirme (pk_test_...) anahtarınızı kullanın veya uygulamanızı Cloudflare'e gönderip canlıda (soru.bitmiyo.com.tr) test edin."
        />
      ) : (
        <ClerkProvider
          localization={trTR}
          publishableKey={PUBLISHABLE_KEY}
          afterSignOutUrl={isLocalhost ? '/' : 'https://bitmiyo.com.tr'}
          appearance={{
            variables: {
              colorPrimary: '#4f46e5',       // indigo-600
              colorText: '#0f172a',
              colorBackground: '#ffffff',
              colorInputBackground: '#f8fafc',
              colorInputText: '#0f172a',
              borderRadius: '0.75rem',
              fontFamily: 'Inter, system-ui, sans-serif'
            },
            layout: {
              socialButtonsPlacement: 'bottom',
              socialButtonsVariant: 'auto',
            },
            elements: {
              card: 'shadow-2xl rounded-3xl border border-slate-100',
              formButtonPrimary: 'font-bold shadow-md',
              formFieldInput: 'border-slate-200 focus:border-indigo-500 rounded-xl',
              headerTitle: 'font-black text-slate-900',
              headerSubtitle: 'text-slate-500',
            }
          }}
        >
          <ToastProvider>
            <App />
          </ToastProvider>
        </ClerkProvider>
      )}
    </HelmetProvider>
  </StrictMode>
);
